import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Anchor, Building2, Gem, Hourglass, Landmark, Megaphone, Ship, Ticket, TrainFront, TriangleAlert } from "lucide-react";
import { sfx } from "@/audio";
import { Face } from "@/city3d/Hud";
import { Btn, Pill } from "./kit";
import { ACC, SHIP, clock, fmt } from "./util";

// A toast: a face or icon, one short line, an amount chip and up to two buttons. `gold` marks a milestone.
function NoticeCard({ id, face, icon: Icon, title, amount, tone = "gold", gold, actions = [] }) {
  return (
    <div className="hud-panel hud-float flex w-[min(92vw,360px)] items-center gap-2.5 rounded-[20px] p-2.5" data-testid="notice"
      style={{ "--acc": ACC[tone], ...(gold && { borderColor: ACC.gold }) }}>
      {face ? (
        <span className="flex shrink-0 -space-x-3">{[].concat(face).slice(0, 3).map((p) => <Face key={p.k} p={p} size={40} ring={ACC[tone]} />)}</span>
      ) : <span className="acc-fill grid h-10 w-10 shrink-0 place-items-center rounded-full"><Icon className="h-5 w-5" strokeWidth={2.4} /></span>}
      <div className="line-clamp-2 min-w-0 flex-1 text-sm font-semibold leading-tight">{title}</div>
      {amount && <Pill acc={tone} className="shrink-0 !text-sm">{amount}</Pill>}
      <div className="flex shrink-0 flex-col gap-1">
        {actions.map(([label, fn], i) => (
          <Btn key={label} tone={i ? "soft" : tone} onClick={() => { toast.dismiss(id); fn(); }}>{label}</Btn>
        ))}
      </div>
    </div>
  );
}

// `sound` is the effect that goes with the toast (false for none); dings are throttled so a pile of toasts doesn't clatter.
let lastDing = 0;
export const card = (props, { sound = "notice", ...opts } = {}) => {
  const id = opts.id ?? `c${Date.now()}${Math.random()}`;
  if (sound && (sound !== "notice" || Date.now() - lastDing > 700)) {
    if (sound === "notice") lastDing = Date.now();
    sfx(sound);
  }
  toast.custom(() => <NoticeCard id={id} {...props} />, { duration: 7000, ...opts, id });
};

export function shipReadyCard(berths, collect) {
  const [first] = berths;
  card({
    icon: SHIP[first.ship] ?? Ship, tone: "blue",
    title: berths.length === 1 ? "Ship in!" : `${berths.length} ships in!`,
    amount: `+${fmt(berths.reduce((s, b) => s + b.pay, 0))}`,
    actions: [["Collect", collect]],
  }, { id: "ready", duration: 9000, sound: "ready" });
}

const NOTES = {
  visit: (n, c) => ({ face: n.p, tone: "green", title: `@${n.p.h} ran a trade to your dock`, amount: `+${n.boost}%`, actions: [["Trade back", () => c.fly(n.p)]] }),
  visits: (n, c) => ({ face: n.ps, tone: "green", title: `${n.ps.length} trades to your dock`, amount: `+${n.boost}%`, actions: [["Trade back", () => c.fly(n.ps[0])]] }),
  uptown: (n, c) => ({ icon: Building2, tone: "green", title: "Uptown!", gold: true, big: true, actions: [["Look", c.home]] }),
  row: (n, c) => ({ icon: Gem, title: `Mansion #${n.rank}`, tone: "rose", gold: true, big: true, actions: [["Go", c.home]] }),
  mayor: (n, c) => ({ icon: Landmark, title: "You're mayor!", gold: true, big: true, actions: [["Hall", c.hall]] }),
  challenge: (n, c) => ({ face: n.p, tone: "rose", title: `@${n.p.h} challenged you`, amount: fmt(n.stake), actions: [["Play", () => c.duels()], ["Trade", () => c.fly(n.p)]] }),
  duel_result: (n, c) => ({
    face: n.p, tone: n.won ? "gold" : "rose", gold: n.won,
    title: n.won ? `You beat @${n.p.h}` : n.won === false ? `@${n.p.h} won` : `Tie with @${n.p.h}`,
    amount: n.won ? `+${fmt(n.prize)}` : n.won === false ? `${n.score ?? 0}-${n.their ?? 0}` : "=",
    actions: [["Rematch", () => c.duels(n.p)]],
  }),
  duel_declined: (n, c) => ({ face: n.p, tone: "rose", title: `@${n.p.h} said no`, actions: [["Trade", () => c.fly(n.p)]] }),
  duel_expired: () => ({ icon: Hourglass, tone: "rose", title: "Duel expired" }),
  quick_cut: (n) => ({ face: n.p, tone: "gold", title: `@${n.p.h} raced your run and lost`, amount: `+${fmt(n.uxd)}` }),
  rent_start: (n, c) => ({ icon: Building2, tone: "green", title: `Uptown is rented by the week now. Yours is ${fmt(n.price)} UXD, first due ${clock(n.due)}`, actions: [["See", c.rent]] }),
  rent_paid: (n) => ({ icon: Building2, tone: "green", title: "Rent paid for another week", amount: `-${fmt(n.price)}` }),
  rent_short: (n, c) => ({ icon: TriangleAlert, tone: "red", title: `Rent of ${fmt(n.price)} is due ${clock(n.due)} and you're short`, actions: [["Dock", c.dock]] }),
  evicted: (n, c) => ({ icon: Anchor, tone: "red", title: "Rent was short, so you're back on the Docks. Your ships kept working", actions: [["Rent", c.rent]] }),
  ad_approved: (n) => ({ icon: Megaphone, tone: "green", title: `Your ${n.slot_name} ad is approved`, amount: Date.parse(n.start) > Date.now() ? clock(n.start) : "Live" }),
  ad_live: (n) => ({ icon: Megaphone, tone: "gold", title: `Your ad is up: ${n.slot_name}` }),
  ad_rejected: (n) => ({ icon: Megaphone, tone: "red", title: `Ad turned down: ${n.reason}`, amount: `+${fmt(n.price)}` }),
  ad_ended: (n) => ({ icon: Megaphone, tone: "grey", title: `Your ${n.slot_name} ad was taken down`, amount: n.refund ? `+${fmt(n.refund)}` : null }),
  freight_back: (n, c) => ({ icon: TrainFront, tone: "gold", title: "Your freight is back from the mainland", amount: `+${fmt(n.uxd)}`, actions: [["Collect", c.dock]] }),
  lottery: (n) => ({ icon: Ticket, tone: "gold", title: "You won the Harbour Lottery!", amount: `+${fmt(n.uxd)}`, gold: true, big: true }),
  grant: (n) => ({ face: n.p, tone: "gold", title: `Mayor @${n.p.h} granted you UXD from the treasury`, amount: `+${fmt(n.uxd)}`, gold: true, big: true }),
};

// The admin's announcement, or the mayor's address when it carries their handle.
function Announcement({ a }) {
  const close = () => toast.dismiss(`a${a.id}`);
  return (
    <div className="hud-panel hud-float w-[min(92vw,380px)] rounded-[20px] p-3" style={{ "--acc": ACC.gold, borderColor: ACC.gold }} data-testid="announcement">
      <div className="flex items-start gap-2.5">
        {a.h ? <Face p={a} size={40} ring={ACC.gold} /> : <span className="acc-fill grid h-10 w-10 shrink-0 place-items-center rounded-full"><Megaphone className="h-5 w-5" strokeWidth={2.4} /></span>}
        <div className="min-w-0 flex-1 self-center leading-snug">
          {a.h && <div className="text-xs font-medium text-x-mute">Mayor @{a.h} to the city</div>}
          <p className="whitespace-pre-line break-words text-sm font-semibold">{a.text}</p>
        </div>
      </div>
      <div className="mt-2.5 flex justify-end gap-1.5">
        {a.link && <Btn tone="gold" onClick={() => { window.open(a.link, "_blank", "noopener"); close(); }}>Open</Btn>}
        <Btn tone={a.link ? "soft" : "gold"} onClick={close} data-testid="announcement-close">Got it</Btn>
      </div>
    </div>
  );
}

// Each open tab checks in twice a minute; the answer is how many are in town, and the admin's announcement and the mayor's
// address, each shown once.
export function usePresence() {
  const [live, setLive] = useState(0);
  useEffect(() => {
    let sid = sessionStorage.getItem("xf_sid");
    if (!sid) sessionStorage.setItem("xf_sid", (sid = Math.random().toString(36).slice(2) + Date.now().toString(36)));
    const ping = () => document.visibilityState === "visible" && api("/presence", { method: "POST", body: { sid } }).then(({ live, ...news }) => {
      setLive(live);
      for (const [key, a] of [["xf_announce", news.announce], ["xf_address", news.address]]) {
        if (!a || localStorage.getItem(key) === a.id) continue;
        localStorage.setItem(key, a.id);
        sfx("notice");
        toast.custom(() => <Announcement a={a} />, { id: `a${a.id}`, duration: Infinity });
      }
    }).catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    document.addEventListener("visibilitychange", ping);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", ping); };
  }, []);
  return live;
}

// Unseen notices show once as toasts (more than two trades fold into one), then are marked seen.
export function useNotices(econ, ctx) {
  const shown = useRef(new Set());
  const c = useRef(ctx);
  c.current = ctx;
  useEffect(() => {
    if (document.visibilityState !== "visible") return;
    let fresh = (econ?.notices || []).filter((n) => NOTES[n.kind] && !shown.current.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => shown.current.add(n.id));
    api("/notices/seen", { method: "POST", body: { ids: fresh.map((n) => n.id) } }).catch(() => {});
    const visits = fresh.filter((n) => n.kind === "visit");
    if (visits.length > 2) fresh = [{ id: `v${visits[0].id}`, kind: "visits", ps: visits.map((n) => n.p), boost: visits[0].boost }, ...fresh.filter((n) => n.kind !== "visit")];
    [...fresh].reverse().forEach((n) => {
      const { big, ...props } = NOTES[n.kind](n, c.current);
      card(props, { id: n.id, duration: props.gold ? 12000 : 8000 });
      if (big) c.current.celebrate();
    });
  }, [econ?.notices]);
}
